import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isAbortError, toZwApiError, type ZwApiError } from '../api/client';
import type { DegradedSource, Slot, SlotDay, SlotsResponse } from '../api/types';
import { useZeitwahl } from '../provider';
import { monthRange, monthsBetween, type ISODate } from '../time/calendar';

export interface UseSlotsOptions {
  org: string;
  eventType: string;
  /** Inclusive `YYYY-MM-DD` range in `tz`; fetched and cached one calendar month at a time. */
  from: ISODate;
  to: ISODate;
  tz: string;
  /** Manage token: excludes that booking's own time and keeps its hosts. */
  reschedule?: string;
  host?: string;
  enabled?: boolean;
}

export interface UseSlotsResult {
  /** Days in `[from, to]` that have at least one slot, sorted. */
  days: SlotDay[];
  slotsByDate: ReadonlyMap<ISODate, Slot[]>;
  availableDates: ReadonlySet<ISODate>;
  degraded: DegradedSource[];
  hostTimeZone: string | null;
  loading: boolean;
  error: ZwApiError | null;
  /** Drops the cache and refetches — call after `slot_unavailable`. */
  refresh: () => void;
}

export function useSlots(options: UseSlotsOptions): UseSlotsResult {
  const { org, eventType, from, to, tz, reschedule, host, enabled = true } = options;
  const { api } = useZeitwahl();
  const cache = useRef(new Map<string, SlotsResponse>());
  const [tick, setTick] = useState(0);
  const [generation, setGeneration] = useState(0);
  const [error, setError] = useState<ZwApiError | null>(null);

  const months = useMemo(() => monthsBetween(from, to), [from, to]);
  const prefix = `${org}|${eventType}|${tz}|${reschedule ?? ''}|${host ?? ''}|`;
  const keys = months.map((m) => prefix + m);
  const keysKey = keys.join(',');
  const pending = enabled && keys.some((k) => !cache.current.has(k));

  useEffect(() => {
    if (!enabled) return;
    const missing = months.filter((m) => !cache.current.has(prefix + m));
    if (missing.length === 0) return;
    const controller = new AbortController();
    setError(null);
    Promise.all(
      missing.map((month) =>
        api
          .getSlots(
            org,
            eventType,
            { ...monthRange(month), tz, reschedule, host },
            { signal: controller.signal }
          )
          .then((res) => {
            cache.current.set(prefix + month, res);
          })
      )
    ).then(
      () => {
        if (!controller.signal.aborted) setTick((t) => t + 1);
      },
      (err: unknown) => {
        if (controller.signal.aborted || isAbortError(err)) return;
        setError(toZwApiError(err));
      }
    );
    return () => controller.abort();
    // `keysKey` covers org, eventType, tz, reschedule, host and the month list.
  }, [api, keysKey, enabled, generation]);

  const refresh = useCallback(() => {
    cache.current.clear();
    setGeneration((g) => g + 1);
  }, []);

  const derived = useMemo(() => {
    const days: SlotDay[] = [];
    const degraded: DegradedSource[] = [];
    let hostTimeZone: string | null = null;
    for (const key of keys) {
      const res = cache.current.get(key);
      if (!res) continue;
      hostTimeZone ??= res.hostTimeZone;
      for (const day of res.days) {
        if (day.date >= from && day.date <= to && day.slots.length > 0) days.push(day);
      }
      if (res.degraded) degraded.push(...res.degraded);
    }
    days.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
    const slotsByDate = new Map(days.map((d) => [d.date, d.slots] as const));
    return {
      days,
      slotsByDate,
      availableDates: new Set(slotsByDate.keys()),
      degraded,
      hostTimeZone,
    };
  }, [keysKey, from, to, tick, generation]);

  return { ...derived, loading: pending && !error, error, refresh };
}
